import { useCallback, useEffect, useMemo, useState } from 'react';

import { CAST_AI } from '@cast/constants';
import {
  useDoesReportingMonetizationApply,
  useTrial,
} from '@cast/shared-trial-ui';
import { Cluster } from '@cast/types';

import { useIsSecuritySunsetEnabled } from 'features/organization/security-insights/hooks/useIsSecuritySunsetEnabled';

import { AssignCredentialsDialogSuccessSlide } from './_components/AssignCredentialsDialogSuccessSlide';
import { AssignCredentialsSetupSlide } from './_components/AssignCredentialsSetupSlide';
import { EnablingCastAiSlide } from './_components/EnablingCastAiSlide';
import { AssignCredentialsDialogV2Context } from './context';
import { AssignCredentialsDialogSlide } from './types';
import { ErrorStep } from '../../../_components/new-onboarding';
import { SlideDialog } from '../../../_components/SlideDialog';
import { useActiveFeatures } from '../../../_hooks/useActiveFeatures';
import {
  useFeatureSelection,
  buildValuesForFeaturePromotion,
  getDefaultFeatureSelection,
} from '../../../_hooks/useFeatureSelection';
import { useWerePhase2FeaturesEnabled } from '../../../_hooks/useWerePhase2FeaturesEnabled';
import { FeatureName, FeatureSelection, ProductFeatures } from '../../../types';

import { ClusterProvider } from '@cast/shared-cluster-context-data-access';

type Props = {
  cluster: Cluster;
  destroy: () => void;
  promotedFeature?: FeatureName;
  onSlideChange?: (slide: AssignCredentialsDialogSlide) => void;
  onSuccess?(enabledFeatures: FeatureSelection): void;
};

export const AssignCredentialsDialogContentV2 = ({
  destroy,
  cluster,
  promotedFeature,
  onSlideChange,
  onSuccess,
}: Props) => {
  const isSecuritySunsetEnabled = useIsSecuritySunsetEnabled();
  const reportingMonetizationApplies = useDoesReportingMonetizationApply();
  const { isPreTrial, isExpired: isTrialExpired } = useTrial();
  const [activeSlideState, setActiveSlideState] =
    useState<AssignCredentialsDialogSlide>('setup');
  const { isLoading: isActiveFeaturesLoading, features: _activeFeatures } =
    useActiveFeatures({
      cluster,
      enabled: activeSlideState === 'setup',
      skipDemoMiddleware: true,
    });

  const alreadyInstalledFeatures: ProductFeatures = useMemo(
    () => ({
      ..._activeFeatures,
      nvidiaGpuPlugin: false,
      kent: false,
    }),
    [_activeFeatures]
  );

  const { featureSelection, updateFeature } = useFeatureSelection({
    initialValues: promotedFeature
      ? buildValuesForFeaturePromotion(promotedFeature, {
          isSecuritySunsetEnabled,
          isTrialExpired,
        })
      : getDefaultFeatureSelection({
          isSecuritySunsetEnabled,
          isTrialExpired,
          reportingMonetizationApplies,
        }),
  });

  useEffect(() => {
    if (activeSlideState !== 'setup') {
      return;
    }

    (Object.keys(alreadyInstalledFeatures) as FeatureName[]).forEach(
      (feature) => {
        if (alreadyInstalledFeatures[feature] && !featureSelection[feature]) {
          updateFeature(feature, true);
        }
      }
    );
  }, [
    alreadyInstalledFeatures,
    activeSlideState,
    featureSelection,
    updateFeature,
  ]);

  const setActiveSlide = useCallback(
    (slide: AssignCredentialsDialogSlide) => {
      setActiveSlideState(slide);
      onSlideChange?.(slide);
      if (slide === 'success') {
        onSuccess?.(featureSelection);
      }
    },
    [featureSelection, onSlideChange, onSuccess]
  );

  const featureEnablementStatus = useWerePhase2FeaturesEnabled({
    cluster,
    featureSelection,
    enabled: activeSlideState === 'inProgress',
    onSuccess: () => setActiveSlide('success'),
    onError: () => setActiveSlide('error'),
  });

  return (
    <AssignCredentialsDialogV2Context.Provider
      value={{
        cluster,
        dismiss: destroy,
        setActiveSlide,
        featureSelection,
        featureEnablementStatus,
        updateFeature,
        alreadyInstalledFeatures,
        promotedFeature,
        isPreTrial,
      }}
    >
      <ClusterProvider cluster={cluster}>
        <SlideDialog
          open
          wideSlides={['setup']}
          onClose={destroy}
          slides={{
            setup: (
              <AssignCredentialsSetupSlide
                isActiveFeaturesLoading={isActiveFeaturesLoading}
              />
            ),
            inProgress: (
              <EnablingCastAiSlide
                onClose={() => destroy()}
                onBack={() => setActiveSlide('setup')}
              />
            ),
            error: (
              <ErrorStep
                title={`Failed to enable ${CAST_AI} features`}
                testId="enabling-features-failed"
                onRetry={() => setActiveSlide('setup')}
                onClose={destroy}
              />
            ),
            success: <AssignCredentialsDialogSuccessSlide cluster={cluster} />,
          }}
          activeSlide={activeSlideState}
          testId="enable-cluster-feature-dialog-content"
        />
      </ClusterProvider>
    </AssignCredentialsDialogV2Context.Provider>
  );
};
